import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { savedQueryService } from '@/services/savedQueryService';
import { CreateSavedQueryRequest, UpdateSavedQueryRequest } from '@/types/savedQuery';
import { useToast } from '@/hooks/use-toast';

export const useSavedQueries = () => {
	return useQuery({
		queryKey: ['saved-queries'],
		queryFn: savedQueryService.getSavedQueries,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

export const useSavedQuery = (id: string | undefined) => {
	return useQuery({
		queryKey: ['saved-query', id],
		queryFn: () => savedQueryService.getSavedQuery(id!),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
	});
};

export const useCreateSavedQuery = () => {
	const queryClient = useQueryClient();
	const { toast } = useToast();

	return useMutation({
		mutationFn: (request: CreateSavedQueryRequest) => savedQueryService.createSavedQuery(request),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
			toast({
				title: "Success",
				description: "Query saved successfully",
			});
		},
		onError: () => {
			toast({
				title: "Error",
				description: "Failed to save query. Please try again.",
				variant: "destructive",
			});
		},
	});
};

export const useUpdateSavedQuery = () => {
	const queryClient = useQueryClient();
	const { toast } = useToast();

	return useMutation({
		mutationFn: ({ id, updates }: { id: string; updates: UpdateSavedQueryRequest }) =>
			savedQueryService.updateSavedQuery(id, updates),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
			queryClient.invalidateQueries({ queryKey: ['saved-query', id] });
			toast({
				title: "Success",
				description: "Query updated successfully",
			});
		},
		onError: () => {
			toast({
				title: "Error",
				description: "Failed to update query. Please try again.",
				variant: "destructive",
			});
		},
	});
};

export const useDeleteSavedQuery = () => {
	const queryClient = useQueryClient();
	const { toast } = useToast();

	return useMutation({
		mutationFn: (id: string) => savedQueryService.deleteSavedQuery(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
			toast({
				title: "Success",
				description: "Query deleted successfully",
			});
		},
		onError: () => {
			toast({
				title: "Error",
				description: "Failed to delete query. Please try again.",
				variant: "destructive",
			});
		},
	});
};

export const useTrackQueryExecution = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => savedQueryService.trackExecution(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
		},
	});
};
